var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { UserType } from "healthex-common/user.dtos";
import { slugifyOptional, unslugifyOptional } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { NavigationHelper } from "./NavigationHelper";
import { SimpleDialogModel } from "./SimpleDialogModel";
const adminPageToRoutes = {
    MY_ORG: NamedRoutes.ADMIN_ORG_SETTINGS,
    MY_USERS: NamedRoutes.ADMIN_USERS,
    DATA_SETTINGS: NamedRoutes.ADMIN_DATA_SETTINGS,
    RESEARCH_SETTINGS: NamedRoutes.ADMIN_RESEARCH_SETTINGS,
    PATIENT_SETTINGS: NamedRoutes.ADMIN_PATIENT_SETTINGS,
    MY_API_KEYS: NamedRoutes.ADMIN_APIKEYS,
};
export class AdminUIManager {
    constructor(routingUIManager, authManager, organizationsManager, routingAuthUIManager) {
        this.routingUIManager = routingUIManager;
        this.authManager = authManager;
        this.organizationsManager = organizationsManager;
        this.routingAuthUIManager = routingAuthUIManager;
        this.navigationHelper = new NavigationHelper(this.routingUIManager, adminPageToRoutes);
        this.goLiveChecklistDialogModel = new SimpleDialogModel();
        this.sectionToExpand = null;
        this.routingUIManager.registerRedirect(NamedRoutes.ADMIN, () => {
            return this.navigationHelper.navigationItemToRoute(this.adminPagesToShow[0].page);
        });
        reaction(() => routingUIManager.currentRoute, async () => {
            this.setSectionToExpand();
        }, { fireImmediately: true });
    }
    get organization() {
        return this.organizationsManager.myOrganization.value;
    }
    get userId() {
        return this.authManager.id;
    }
    async updatePatientSettingsPatientAuthentication(orgUpdates) {
        try {
            if (orgUpdates) {
                await this.updateHealthcareOrganizationDetails(orgUpdates);
            }
        }
        catch (e) {
            console.log(e);
        }
    }
    async updateHealthcareOrganizationDetails(update) {
        if (this.authManager.userType !== UserType.HEALTHCARE_ORGANIZATION)
            return;
        if (this.authManager.orgId === null)
            return;
        if (this.authManager.id === null)
            return;
        if (this.organization === NOT_LOADED || this.organization === null)
            return;
        await this.organizationsManager.updateOrganizationDetails(this.authManager.orgId, update);
        await this.organizationsManager.waitForReloadOrganizations();
    }
    get currentAdminPage() {
        var _a, _b;
        return (_b = (_a = this.navigationHelper) === null || _a === void 0 ? void 0 : _a.currentNavigationItem) !== null && _b !== void 0 ? _b : null;
    }
    setCurrentAdminPage(adminPage, section) {
        var _a, _b;
        (_a = this.navigationHelper) === null || _a === void 0 ? void 0 : _a.navigateTo(adminPage, {
            section: `${(_b = slugifyOptional(section)) !== null && _b !== void 0 ? _b : ""}`,
        });
    }
    setSectionToExpand() {
        var _a, _b, _c;
        this.sectionToExpand = (_c = unslugifyOptional((_b = (_a = this.routingUIManager) === null || _a === void 0 ? void 0 : _a.currentRouteParams) === null || _b === void 0 ? void 0 : _b.section)) !== null && _c !== void 0 ? _c : null;
    }
    get organizationId() {
        return this.authManager.orgId;
    }
    get organizationType() {
        return this.authManager.userType;
    }
    get isHealthcareOrganizationUser() {
        return this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION;
    }
    get organizationIsLive() {
        return this.authManager.organizationIsLive;
    }
    get userIdToShowDetailsFor() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.userId) !== null && _b !== void 0 ? _b : null;
    }
    get keyIdToShowDetailsFor() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.keyId) !== null && _b !== void 0 ? _b : "null";
    }
    get adminPagesToShow() {
        const pages = [
            { page: "MY_ORG", label: "Organization settings" },
            { page: "MY_USERS", label: "Users" },
            { page: "DATA_SETTINGS", label: "Data settings" },
            { page: "RESEARCH_SETTINGS", label: "Research settings" },
            { page: "PATIENT_SETTINGS", label: "Patient settings" },
            { page: "MY_API_KEYS", label: "API keys" },
        ];
        return pages.filter((item) => this.routingAuthUIManager.isRouteAuthorized(this.navigationHelper.navigationItemToRoute(item.page)));
    }
    navigateToUserDetails(userId) {
        this.routingUIManager.navigateTo(NamedRoutes.ADMIN_USERS_DETAIL, { userId });
    }
    openGoLiveChecklistDialogModel() {
        if (this.organizationIsLive === false) {
            this.goLiveChecklistDialogModel.open = true;
        }
    }
    closeGoLiveChecklistDialogModel() {
        this.goLiveChecklistDialogModel.open = false;
    }
}
__decorate([
    observable
], AdminUIManager.prototype, "sectionToExpand", void 0);
__decorate([
    computed
], AdminUIManager.prototype, "organization", null);
__decorate([
    computed
], AdminUIManager.prototype, "userId", null);
__decorate([
    computed
], AdminUIManager.prototype, "currentAdminPage", null);
__decorate([
    computed
], AdminUIManager.prototype, "organizationId", null);
__decorate([
    computed
], AdminUIManager.prototype, "organizationType", null);
__decorate([
    computed
], AdminUIManager.prototype, "isHealthcareOrganizationUser", null);
__decorate([
    computed
], AdminUIManager.prototype, "organizationIsLive", null);
__decorate([
    computed
], AdminUIManager.prototype, "userIdToShowDetailsFor", null);
__decorate([
    computed
], AdminUIManager.prototype, "keyIdToShowDetailsFor", null);
__decorate([
    computed
], AdminUIManager.prototype, "adminPagesToShow", null);
